import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase, testSupabaseConnection } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';

// Types for analysis data
export interface ContentAnalysis {
  id: string;
  title: string;
  video_url: string;
  thumbnail_url: string | null;
  upload_date: string;
  status: 'processing' | 'completed' | 'failed';
  grade: string | null;
  predicted_average_watch_percentage?: number;
  predicted_major_dropoff_timestamp?: string;
  estimated_views_range_as_is?: string | null;
  created_at: string;
  updated_at: string;
}

interface AnalyticsContextType {
  analyses: ContentAnalysis[];
  loading: boolean;
  uploadVideo: (file: File, title: string) => Promise<string>;
  getAnalysis: (id: string) => ContentAnalysis | undefined;
  getRecentAnalyses: () => ContentAnalysis[];
  setAnalyses: React.Dispatch<React.SetStateAction<ContentAnalysis[]>>;
  refreshAnalyses: () => Promise<void>;
  forceUpdate: (analysisId: string) => Promise<void>;
}

export const AnalyticsContext = createContext<AnalyticsContextType>({
  analyses: [],
  loading: true,
  uploadVideo: async () => '',
  getAnalysis: () => undefined,
  getRecentAnalyses: () => [],
  setAnalyses: () => {},
  refreshAnalyses: async () => {},
  forceUpdate: async () => {}
});

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

// Cache for analyses data
const analysesCache = new Map<string, {
  data: ContentAnalysis[];
  timestamp: number;
}>();

// Cache TTL (5 minutes)
const ANALYSES_CACHE_TTL = 2 * 60 * 1000; // 2 minutes
// Minimum time between refreshes (1 second)
const MIN_REFRESH_INTERVAL = 1000; // 1 second

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children }) => {
  const [analyses, setAnalyses] = useState<ContentAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, session } = useAuth(); // Get auth state
  const [lastError, setLastError] = useState<Error | null>(null);
  const realtimeSubscriptionRef = useRef<any>(null);
  const lastRefreshTimeRef = useRef(0);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Helper function to log errors and set state
  const handleError = (error: any, message: string) => {
    const errorObj = error instanceof Error ? error : new Error(String(error));
    console.error(`${message}:`, errorObj);
    setLastError(errorObj);
    return errorObj;
  };

  // Check database connection
  const checkConnection = async () => {
    try {
      const connected = await testSupabaseConnection();
      return connected;
    } catch (err) {
      handleError(err, 'Database connection check failed');
      return false;
    }
  };

  // Force update a specific analysis
  const forceUpdate = useCallback(async (analysisId: string) => {
    if (!user || !analysisId) return;
    
    
    try {
      const { data, error } = await supabase
        .from('content_analyses')
        .select('*')
        .eq('id', analysisId)
        .maybeSingle();
        
      if (error) {
        console.error('Error fetching analysis update:', error);
        return;
      }
      
      if (data) {
        
        setAnalyses(prev => {
          // Find if this analysis exists in the current list
          const existingIndex = prev.findIndex(a => a.id === analysisId);
          
          if (existingIndex >= 0) {
            // Replace the existing analysis with updated data
            const newAnalyses = [...prev];
            newAnalyses[existingIndex] = data;
            return newAnalyses;
          } else {
            // Add as a new analysis if not found
            return [data, ...prev];
          }
        });
      }
    } catch (err) {
      console.error('Error during force update:', err);
    }
  }, [user]);

  const refreshAnalyses = useCallback(async () => {
    if (!user) {
      setAnalyses([]);
      setLoading(false);
      return;
    }

    const now = Date.now();
    if (now - lastRefreshTimeRef.current < MIN_REFRESH_INTERVAL) {
      return;
    }
    lastRefreshTimeRef.current = now;

    const cachedData = analysesCache.get(user.id);
    if (cachedData && (now - cachedData.timestamp < ANALYSES_CACHE_TTL)) {
      setAnalyses(prev => prev.length === 0 ? cachedData.data : prev);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const { data, error } = await supabase
        .from('content_analyses')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      setAnalyses(data || []);

      analysesCache.set(user.id, {
        data: data || [],
        timestamp: now
      });

      setLastError(null);
      setInitialLoadComplete(true);
    } catch (error) {
      handleError(error, 'Error refreshing analyses');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;

    refreshAnalyses();

    if (realtimeSubscriptionRef.current) {
      realtimeSubscriptionRef.current.unsubscribe();
    }

    const subscription = supabase
      .channel(`content_analyses_${user.id}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'content_analyses',
        filter: `user_id=eq.${user.id}`
      }, payload => {
        if (payload.eventType === 'INSERT') {
          setAnalyses(prev => {
            const exists = prev.some(a => a.id === payload.new.id);
            if (exists) return prev;
            return [payload.new as ContentAnalysis, ...prev];
          });
        } else if (payload.eventType === 'UPDATE') {
          setAnalyses(prev =>
            prev.map(analysis =>
              analysis.id === payload.new.id ? payload.new as ContentAnalysis : analysis
            )
          );
        } else if (payload.eventType === 'DELETE') {
          setAnalyses(prev =>
            prev.filter(analysis => analysis.id !== payload.old.id)
          );
        }
      })
      .subscribe();

    realtimeSubscriptionRef.current = subscription;

    return () => {
      subscription.unsubscribe();
      realtimeSubscriptionRef.current = null;
    };
  }, [user]);

  // Expose functions for debugging
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).refreshAnalyses = refreshAnalyses;
      (window as any).forceUpdateAnalysis = forceUpdate;
    }
  }, [refreshAnalyses, forceUpdate]);

  // Upload a new video
  const uploadVideo = async (file: File, title: string): Promise<string> => {
    setLoading(true);
    try {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) throw new Error('User not authenticated');

      // Upload video to storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `public/${user.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('videos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get video URL
      const { data: { publicUrl } } = supabase.storage
        .from('videos')
        .getPublicUrl(filePath);

      // Create analysis entry
      const { data: analysis, error: dbError } = await supabase
        .from('content_analyses')
        .insert([
          {
            user_id: user.id,
            title,
            video_url: publicUrl,
            storage_id: fileName.split('.')[0],
            filename: file.name,
            status: 'processing'
          }
        ])
        .select()
        .single();

      if (dbError) throw dbError;
      
      
      // Update cache with new analysis
      const cachedData = analysesCache.get(user.id);
      if (cachedData) {
        analysesCache.set(user.id, {
          data: [analysis, ...cachedData.data],
          timestamp: Date.now()
        });
      }
      
      return analysis.id;
    } catch (error) {
      throw handleError(error, 'Upload error');
    } finally {
      setLoading(false);
    }
  };

  // Get a specific analysis by ID using the local cache
  const getAnalysis = (id: string): ContentAnalysis | undefined => {
    return analyses.find(analysis => analysis.id === id);
  };

  // Get recent analyses
  const getRecentAnalyses = (): ContentAnalysis[] => {
    return analyses.slice(0, 5);
  };

  return (
    <AnalyticsContext.Provider 
      value={{ 
        analyses, 
        loading, 
        uploadVideo, 
        getAnalysis,
        getRecentAnalyses,
        setAnalyses,
        refreshAnalyses,
        forceUpdate
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
};